import { useMemo } from "react";
import type {
  CraftableWeapon,
  CraftingComponent,
  CraftingSocketKey,
} from "@/lib/crafting/types";
import type { SelectedComponent } from "@/lib/crafting/engine";
import { NUMERIC_STATS, STAT_DISPLAY_ORDER } from "@/lib/bungie/assets";

export interface StatBlockProps {
  weapon: CraftableWeapon;
  /** The components currently in the plan. */
  selected: SelectedComponent[];
  /** The component under the pointer, and the column it would replace. */
  preview?: { key: CraftingSocketKey; component: CraftingComponent };
}

/** Weapon bar stats run 0–100. */
const BAR_MAX = 100;

function sumStats(components: { stats?: Record<number, number> }[]): Map<number, number> {
  const totals = new Map<number, number>();
  for (const c of components) {
    for (const [hash, value] of Object.entries(c.stats ?? {})) {
      const key = Number(hash);
      totals.set(key, (totals.get(key) ?? 0) + value);
    }
  }
  return totals;
}

/**
 * The stat readout, with the game's green/red preview bars.
 *
 * Hovering a component shows what swapping it in would do: the shared portion
 * of the bar stays white, and the difference is drawn as a green extension
 * (gain) or a red segment carved off the end (loss). The numeric delta is always
 * printed alongside, so the change is never conveyed by colour alone.
 */
export function StatBlock({ weapon, selected, preview }: StatBlockProps) {
  const rows = useMemo(() => {
    const currentMods = sumStats(selected.map((s) => s.component));

    // The previewed loadout: the plan with the hovered component swapped into
    // its column, so the comparison is like-for-like rather than additive.
    const previewMods = preview
      ? sumStats([
          ...selected.filter((s) => s.key !== preview.key).map((s) => s.component),
          preview.component,
        ])
      : currentMods;

    return weapon.stats
      // Allow-list: the manifest also carries internal stats (Attack, Power,
      // Ammo Generation) that the in-game inspect screen does not display.
      .filter((stat) => STAT_DISPLAY_ORDER.includes(stat.statHash))
      .sort(
        (a, b) => STAT_DISPLAY_ORDER.indexOf(a.statHash) - STAT_DISPLAY_ORDER.indexOf(b.statHash)
      )
      .map((stat) => {
        const current = Math.max(0, stat.value + (currentMods.get(stat.statHash) ?? 0));
        const previewed = Math.max(0, stat.value + (previewMods.get(stat.statHash) ?? 0));
        return {
          ...stat,
          current,
          previewed,
          delta: previewed - current,
          numeric: NUMERIC_STATS.has(stat.statHash),
        };
      });
  }, [weapon.stats, selected, preview]);

  if (rows.length === 0) return null;

  return (
    <dl className="space-y-px text-sm">
      {rows.map((stat) => {
        const gain = stat.delta > 0;
        // The bar's shared base is whatever both loadouts have in common.
        const shared = Math.min(stat.current, stat.previewed);
        const changed = stat.delta !== 0;

        return (
          <div key={stat.statHash} className="flex items-center justify-end gap-3">
            <dt className="text-right text-ink-2">{stat.name}</dt>

            {stat.numeric ? (
              <dd className="w-28 text-right font-semibold tabular-nums">
                {stat.previewed.toLocaleString()}
              </dd>
            ) : (
              <dd className="flex w-28 items-center gap-2">
                <span aria-hidden className="flex h-2 flex-1 bg-white/12">
                  <span
                    className="block h-full bg-white/85 transition-[width] duration-150 ease-(--ease-crisp)"
                    style={{ width: `${Math.min(100, (shared / BAR_MAX) * 100)}%` }}
                  />
                  {changed && (
                    <span
                      className={`block h-full transition-[width] duration-150 ease-(--ease-crisp) ${gain ? "bg-ok" : "bg-short"}`}
                      style={{
                        width: `${Math.min(100, (Math.abs(stat.delta) / BAR_MAX) * 100)}%`,
                      }}
                    />
                  )}
                </span>
                <span className="w-8 text-right font-semibold tabular-nums">
                  {stat.previewed}
                </span>
              </dd>
            )}

            {/* Printed delta: the change must not depend on the bar's colour. */}
            <span
              className={`w-9 text-xs tabular-nums ${
                changed ? (gain ? "text-ok" : "text-short") : "text-transparent"
              }`}
            >
              {changed ? `${gain ? "+" : "−"}${Math.abs(stat.delta)}` : "0"}
              {changed && (
                <span className="sr-only">
                  {gain ? " increase" : " decrease"} if you select this component
                </span>
              )}
            </span>
          </div>
        );
      })}
    </dl>
  );
}
